import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { m, useReducedMotion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ImageOff,
  Layers3,
  Pause,
  Play,
  ShieldCheck,
} from "lucide-react";
import { BrandCopy } from "../../components/BrandWordmarkText";
import BrandWordmarkText from "../../components/BrandWordmarkText";
import ButtonLink from "../../components/marketing/ButtonLink";
import { useMarketingMotion } from "../../components/marketing/MarketingMotion";
import { isPlainBookDemoActivation } from "../../components/marketing/shared";
import { Button } from "../../components/ui/primitives";
import { cn } from "../../lib/utils";
import { PRODUCT_STORY_SCENES, productStoryCropFor, attachAvifSource } from "./productStoryContent";

const EASE_OUT = [0.23, 1, 0.32, 1];
const SCENE_COUNT = PRODUCT_STORY_SCENES.length;
// ONE sizes vocabulary shared by the visible scene <img> and warmScene's preloader, so the
// preload warms EXACTLY the srcset candidate the browser will render (PERF-5/VIS-2) — never the
// 812–995KB native master on a viewport that renders a downscaled rung.
//
// PERF-9: these widths are MEASURED from the layout rather than guessed, because a `sizes` lie
// is paid for on every load. The frame is `w-full` inside `mx-auto max-w-7xl px-5 sm:px-6 lg:px-8`,
// and the media stage adds `px-0 sm:px-5 xl:px-12` plus `xl:pl-2`:
//   <640px   : 100vw − 40px  (container px-5)
//   640-1023 : 100vw − 88px  (container px-6 + stage px-5)
//   1024-1279: 100vw − 104px (container px-8 + stage px-5)
//   ≥1280px  : the container caps at max-w-7xl and the stage becomes the 0.63fr grid column, so
//              the frame settles at a FIXED ~637px — 0.63 × (1216 − 40 gap) − 8 (pl-2) − 96 (px-12).
// The old string declared 58vw at ≥1280 (742px at 1280, 1114px at 1920) and 100vw on phones, so a
// DPR-2 laptop fetched the lossless master and a 390px phone fetched w960.
export const PRODUCT_STORY_IMAGE_SIZES =
  "(min-width: 1280px) 637px, (min-width: 1024px) calc(100vw - 104px), (min-width: 640px) calc(100vw - 88px), calc(100vw - 40px)";
// PERF-9: below xl the detail crops render in a TWO-column grid (`grid-cols-2 gap-3 xl:hidden`)
// inside the same media stage, so each crop is roughly half the stage width — not the 92vw/46vw the
// old string claimed. Declaring ~2x the real width made a DPR-2 phone fetch ~4x the pixels it drew.
//   <640px   : (100vw − 40px − 12px gap) / 2  = 50vw − 26px
//   640-1279 : (100vw − 88px − 12px gap) / 2  = 50vw − 50px
// At xl the crops become absolutely-positioned orbit cards at `w-[40%]` of the ~637px frame (~255px);
// 320px stays the declared value there because it selects the same rung and is the reviewed default.
const CROP_SIZES = Object.freeze({
  mobile: "(min-width: 640px) calc(50vw - 50px), calc(50vw - 26px)",
  // The THIRD mobile crop spans both grid columns (col-span-2 below), so its rendered width is
  // the full stage, not half — declaring the half-width formula for it made a DPR-2 phone fetch
  // a tier that covered only ~69% of the drawn pixels (caught by responsive-story-media's
  // density gate the first time it ran against the AVIF registry).
  mobileSpan: "(min-width: 640px) calc(100vw - 88px), calc(100vw - 40px)",
  orbit: "320px",
});
export const PRODUCT_STORY_AUTOPLAY_MS = 3000;
export const PRODUCT_STORY_AUTO_MEDIA_QUERY = "(min-width: 1024px) and (hover: hover) and (pointer: fine)";
const TAB_FOCUS_KEYS = new Set(["ArrowLeft", "ArrowRight", "Home", "End"]);
const TAB_ACTIVATION_KEYS = new Set(["Enter", " "]);
const ORBIT_CLASSES = Object.freeze({
  "top-left": "left-0 top-0 w-[40%]",
  "top-right": "right-0 top-0 w-[35%]",
  right: "right-0 top-[30%] w-[34%]",
  "bottom-left": "bottom-0 left-[5%] w-[39%]",
  "bottom-right": "bottom-0 right-[3%] w-[38%]",
});

function shouldAutoPlayByDefault() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(PRODUCT_STORY_AUTO_MEDIA_QUERY).matches;
}

function clockNow() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

function storyNumber(index) {
  return String(index + 1).padStart(2, "0");
}

function storyAccent(scene, opacity) {
  return `rgb(var(${scene.accent})${opacity == null ? "" : ` / ${opacity}`})`;
}

function contextualCta(cta, catalogueTargetId) {
  if (
    !cta?.to
    || !catalogueTargetId
    || !/^\/modules(?:\?|$)/.test(cta.to)
  ) return cta;
  return { ...cta, to: `${cta.to}#${catalogueTargetId}` };
}

function StoryCta({ cta, sceneId, emphasis, onBookDemo }) {
  const classes = "min-h-12 w-full justify-center rounded-full px-5 text-[14px] font-semibold transition-[transform,opacity] duration-150 ease-premium sm:w-auto";

  if (cta.to) {
    return (
      <ButtonLink
        to={cta.to}
        variant={emphasis ? "primary" : "outline"}
        size="lg"
        className={classes}
        onClick={cta.action === "demo" ? (event) => {
          // Keep the canonical landing URL for no-JS and modified/new-tab navigation. In the live
          // marketing experience, a plain activation opens the contextual dialog in place.
          if (!isPlainBookDemoActivation(event)) return;
          event.preventDefault();
          onBookDemo?.(sceneId);
        } : undefined}
        data-testid={`product-story-cta-${sceneId}-${emphasis ? "primary" : "secondary"}`}
      >
        {cta.label}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </ButtonLink>
    );
  }

  return (
    <Button
      type="button"
      variant={emphasis ? "primary" : "outline"}
      size="lg"
      className={classes}
      onClick={() => onBookDemo?.(sceneId)}
      data-testid={`product-story-cta-${sceneId}-${emphasis ? "primary" : "secondary"}`}
    >
      {cta.label}
      <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </Button>
  );
}

function transitionFor(mode, direction) {
  if (mode === "paused" || mode === "keyboard" || mode === "reduced") {
    return {
      initial: false,
      animate: { opacity: 1, transform: "translate3d(0,0,0)" },
      transition: { duration: 0 },
    };
  }
  return {
    initial: {
      opacity: 0,
      transform: `translate3d(${direction > 0 ? 14 : -14}px,0,0)`,
    },
    animate: { opacity: 1, transform: "translate3d(0,0,0)" },
    transition: { duration: 0.21, ease: EASE_OUT },
  };
}

function fragmentTransitionFor(mode, index, direction) {
  if (mode === "paused" || mode === "keyboard" || mode === "reduced") {
    return {
      initial: false,
      animate: { opacity: 1, transform: "translate3d(0,0,0)" },
      transition: { duration: 0 },
    };
  }
  return {
    initial: {
      opacity: 0,
      transform: `translate3d(0,${direction > 0 ? 8 : -8}px,0)`,
    },
    animate: { opacity: 1, transform: "translate3d(0,0,0)" },
    transition: { duration: 0.15, delay: index * 0.035, ease: EASE_OUT },
  };
}

function ExactProductCrop({ scene, fragment, mediaEnabled, mobile = false, mobileSpan = false }) {
  const { crop } = fragment;
  const cropMedia = productStoryCropFor(scene, fragment);

  return (
    <div
      className="relative w-full overflow-hidden bg-surface"
      style={{ aspectRatio: `${cropMedia.width} / ${cropMedia.height}` }}
      data-crop={`${crop.x},${crop.y},${crop.w},${crop.h}`}
      data-crop-source={scene.image}
    >
      {mediaEnabled && (
        <picture className="contents">
          <source type="image/avif" srcSet={cropMedia.avifSrcSet} sizes={CROP_SIZES[mobile ? (mobileSpan ? "mobileSpan" : "mobile") : "orbit"]} />
          <img
            src={cropMedia.image}
            srcSet={cropMedia.srcSet}
            sizes={CROP_SIZES[mobile ? (mobileSpan ? "mobileSpan" : "mobile") : "orbit"]}
            alt=""
            aria-hidden="true"
            width={cropMedia.width}
            height={cropMedia.height}
            loading="lazy"
            decoding="async"
            draggable="false"
            data-image-treatment="sharp-unfiltered-crop"
            className="pointer-events-none block h-auto w-full select-none object-contain [filter:none] [image-rendering:auto] [transform:none] transition-none"
          />
        </picture>
      )}
    </div>
  );
}

function ProductFragment({ scene, fragment, index, mode, direction, mediaEnabled, mobile, mobileSpan = false }) {
  const feature = scene.featureCards[fragment.featureIndex];
  const Icon = feature?.icon;
  const motionProps = fragmentTransitionFor(mode, index, direction);

  return (
    <m.figure
      {...motionProps}
      className={cn(
        "product-story-fragment min-w-0 overflow-hidden rounded-xl border border-line/80 bg-canvas shadow-[0_18px_55px_-32px_rgba(15,23,42,0.7)]",
        mobile ? "relative" : cn("absolute z-20 hidden xl:block", ORBIT_CLASSES[fragment.orbitSlot] || ORBIT_CLASSES.right),
      )}
      data-testid={`product-story-fragment-${scene.id}-${fragment.id}`}
    >
      <ExactProductCrop scene={scene} fragment={fragment} mediaEnabled={mediaEnabled} mobile={mobile} mobileSpan={mobileSpan} />
      <figcaption className="flex min-h-10 items-center gap-2 border-t border-line/70 bg-canvas/95 px-2.5 py-2 text-[10px] font-semibold leading-tight text-ink sm:text-[11px]">
        {Icon && (
          <span
            className="grid h-6 w-6 shrink-0 place-items-center rounded-md"
            style={{ color: storyAccent(scene), backgroundColor: storyAccent(scene, 0.08) }}
            aria-hidden="true"
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={1.9} />
          </span>
        )}
        <span>{fragment.label}</span>
      </figcaption>
    </m.figure>
  );
}

function StoryMedia({ scene, priority = false, mediaEnabled, onMediaSettled, swipeHandlers }) {
  const [state, setState] = useState(mediaEnabled ? "loading" : "deferred");
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    setState(mediaEnabled ? "loading" : "deferred");
  }, [mediaEnabled, scene.image]);

  const finishLoad = async (event) => {
    try {
      await event.currentTarget.decode?.();
    } catch {
      // A decoded image may race a tab change; the loaded bitmap is still safe to paint.
    }
    if (mounted.current) {
      setState("ready");
      onMediaSettled?.(scene.id);
    }
  };

  const failLoad = () => {
    if (!mounted.current) return;
    setState("error");
    // The narrative and explicit fallback remain usable, so a failed illustration must not
    // freeze automatic playback on the same chapter forever.
    onMediaSettled?.(scene.id);
  };

  return (
    <figure
      className="min-w-0"
      data-testid={`product-story-media-${scene.id}`}
      data-media-state={state}
      data-presentation="open-product-stage"
    >
      <div
        className="product-story-main-frame relative w-full touch-pan-y select-none overflow-hidden rounded-xl bg-surface shadow-[0_34px_90px_-50px_rgba(15,23,42,0.72)]"
        data-theme-stage="static-light"
        style={{ aspectRatio: `${scene.imageWidth} / ${scene.imageHeight}` }}
        {...swipeHandlers}
      >
        {(state === "loading" || state === "deferred") && (
          <div className="absolute inset-0 grid place-items-center" aria-hidden="true">
            <div className="h-10 w-28 rounded-full bg-primary/10" />
          </div>
        )}
        {state === "error" ? (
          <div className="absolute inset-0 grid place-items-center p-6 text-center">
            <div>
              <ImageOff className="mx-auto h-6 w-6 text-ink-subtle" aria-hidden="true" />
              <p className="mt-3 text-sm font-semibold text-ink">Product visual unavailable</p>
              <p className="mt-1 text-xs leading-relaxed text-ink-subtle">The chapter text and its links still work.</p>
            </div>
          </div>
        ) : mediaEnabled ? (
          <picture className="contents">
            {/* PERF-9: AVIF first. `contents` keeps <picture> boxless so the absolutely
                positioned <img> below keeps the frame as its containing block. */}
            <source type="image/avif" srcSet={scene.imageAvifSrcSet} sizes={PRODUCT_STORY_IMAGE_SIZES} />
            <img
              src={scene.image}
              srcSet={scene.imageSrcSet}
              alt={scene.imageAlt}
              width={scene.imageWidth}
              height={scene.imageHeight}
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : "auto"}
              decoding="async"
              draggable="false"
              sizes={PRODUCT_STORY_IMAGE_SIZES}
              onLoad={finishLoad}
              onError={failLoad}
              data-image-treatment="sharp-unfiltered"
              className={cn(
                "pointer-events-none absolute inset-0 h-full w-full select-none object-contain object-center [filter:none] [image-rendering:auto] [transform:none] transition-none",
                state === "ready" ? "visible" : "invisible",
              )}
            />
          </picture>
        ) : null}
      </div>
      <figcaption className="mt-3 flex flex-col gap-1.5 text-[11px] leading-relaxed text-ink-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <span className="flex min-w-0 items-start gap-1.5 font-semibold">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-positive" aria-hidden="true" />
          <span>Illustrative product visual <span aria-hidden="true">·</span> synthetic sample data <span aria-hidden="true">·</span> not a live customer screen</span>
        </span>
        <span className="sm:max-w-[22rem] sm:text-right">{scene.availability}</span>
      </figcaption>
    </figure>
  );
}

function SceneCopy({ scene, index, onBookDemo, catalogueTargetId }) {
  const primaryCTA = contextualCta(scene.primaryCTA, catalogueTargetId);
  const secondaryCTA = contextualCta(scene.secondaryCTA, catalogueTargetId);

  return (
    <div className="product-story-scene-copy min-w-0 xl:pr-4">
      <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.16em] text-ink-subtle">
        <span className="tabular-nums" style={{ color: storyAccent(scene) }}>
          Scene {storyNumber(index)} of {SCENE_COUNT}
        </span>
        <span className="h-px w-8 bg-line" aria-hidden="true" />
        <span>{scene.eyebrow}</span>
      </div>

      <h3
        id={`product-story-scene-${scene.id}-heading`}
        className="mt-4 max-w-[13ch] text-balance font-heading text-[clamp(2rem,4vw,3.6rem)] font-bold leading-[1.01] tracking-[-0.045em] text-ink"
      >
        {scene.title}{" "}
        <span style={{ color: storyAccent(scene) }}>{scene.highlightedTitle}</span>
      </h3>

      <div className="mt-5 border-l-2 pl-4" style={{ borderColor: storyAccent(scene, 0.55) }}>
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-subtle">What goes wrong today</p>
        <p className="mt-1.5 text-[13px] leading-6 text-ink-2"><BrandCopy>{scene.problem}</BrandCopy></p>
      </div>

      <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-subtle">The connected response</p>
      <p className="mt-2 text-pretty text-[15px] leading-7 text-ink-2 sm:text-base">
        <BrandCopy>{scene.description}</BrandCopy>
      </p>

      <ul className="mt-5 space-y-2.5" aria-label={`${scene.eyebrow} benefits`}>
        {scene.benefits.map((benefit) => (
          <li key={benefit} className="flex items-start gap-3 text-[13.5px] leading-6 text-ink-2 sm:text-sm">
            <span
              className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full"
              style={{ backgroundColor: storyAccent(scene, 0.08), color: storyAccent(scene) }}
              aria-hidden="true"
            >
              <Check className="h-3 w-3" strokeWidth={2.5} />
            </span>
            <BrandCopy>{benefit}</BrandCopy>
          </li>
        ))}
      </ul>

      <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-line pt-5" aria-label={`${scene.eyebrow} capabilities`}>
        {scene.featureCards.map((feature) => {
          const Icon = feature.icon;
          return (
            <li key={feature.title} className="flex min-w-0 items-center gap-2 text-xs font-semibold leading-snug text-ink-2">
              <Icon className="h-4 w-4 shrink-0" style={{ color: storyAccent(scene) }} strokeWidth={1.9} aria-hidden="true" />
              <span>{feature.title}</span>
              <span className="sr-only">: {feature.description}</span>
            </li>
          );
        })}
      </ul>

      <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <StoryCta cta={primaryCTA} sceneId={scene.id} emphasis onBookDemo={onBookDemo} />
        {secondaryCTA && (
          <StoryCta cta={secondaryCTA} sceneId={scene.id} onBookDemo={onBookDemo} />
        )}
      </div>
    </div>
  );
}

function SceneMediaStage({
  scene,
  index,
  mode,
  direction,
  mediaEnabled,
  onMediaSettled,
  swipeHandlers,
}) {
  return (
    <div className="product-story-media-stage min-w-0 xl:pl-2">
      <div className="relative px-0 pb-3 pt-10 sm:px-5 sm:pb-6 sm:pt-12 xl:px-12 xl:pb-16 xl:pt-16">
        <div
          className="pointer-events-none absolute inset-[12%_5%_8%] rounded-[50%] opacity-60 blur-3xl"
          style={{ backgroundColor: storyAccent(scene, 0.12) }}
          aria-hidden="true"
        />
        <div className="absolute left-1 top-0 z-10 flex items-center gap-3 sm:left-6 xl:left-12">
          <BrandWordmarkText className="text-[11px]" />
          <span className="h-4 w-px bg-line" aria-hidden="true" />
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-subtle">{scene.eyebrow}</span>
        </div>

        <div className="relative z-10">
          <StoryMedia
            scene={scene}
            priority={index === 0}
            mediaEnabled={mediaEnabled}
            onMediaSettled={onMediaSettled}
            swipeHandlers={swipeHandlers}
          />
        </div>

        {scene.visualFragments.map((fragment, fragmentIndex) => (
          <ProductFragment
            key={fragment.id}
            scene={scene}
            fragment={fragment}
            index={fragmentIndex}
            mode={mode}
            direction={direction}
            mediaEnabled={mediaEnabled}
          />
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 xl:hidden" aria-label={`${scene.eyebrow} interface details`}>
        {scene.visualFragments.map((fragment, fragmentIndex) => (
          <div key={fragment.id} className={fragmentIndex === 2 ? "col-span-2" : undefined}>
            <ProductFragment
              scene={scene}
              fragment={fragment}
              index={fragmentIndex}
              mode={mode}
              direction={direction}
              mediaEnabled={mediaEnabled}
              mobileSpan={fragmentIndex === 2}
              mobile
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ProductStoryShowcase({
  onBookDemo,
  sectionId = "product-story",
  skipTargetId = "modules",
  skipLabel = "Skip to the full module catalogue",
  catalogueTargetId = skipTargetId === "module-catalogue" ? skipTargetId : null,
}) {
  const headingId = useId();
  const tabsId = useId();
  const sectionRef = useRef(null);
  const stageViewportRef = useRef(null);
  const tabRefs = useRef([]);
  const swipeStart = useRef(null);
  const scenePreloaders = useRef(new Map());
  const playbackTimer = useRef(null);
  const playbackStartedAt = useRef(null);
  const playbackRemaining = useRef(PRODUCT_STORY_AUTOPLAY_MS);
  const progressLineRef = useRef(null);
  const progressAnimation = useRef(null);
  const rotationControlPointerDown = useRef(false);
  const prefersReducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const [focusIndex, setFocusIndex] = useState(0);
  const [settledSceneIds, setSettledSceneIds] = useState(() => new Set());
  const [autoPlayRequested, setAutoPlayRequested] = useState(shouldAutoPlayByDefault);
  const [focusPauseLocked, setFocusPauseLocked] = useState(false);
  const [sectionInView, setSectionInView] = useState(false);
  const [mediaActivated, setMediaActivated] = useState(false);
  const [documentVisible, setDocumentVisible] = useState(() => (
    typeof document === "undefined" || document.visibilityState !== "hidden"
  ));
  const [pointerInside, setPointerInside] = useState(false);
  const [activeState, setActiveState] = useState({
    index: 0,
    direction: 1,
    input: "pointer",
    token: 0,
  });
  const activeScene = PRODUCT_STORY_SCENES[activeState.index];
  const activeMediaSettled = settledSceneIds.has(activeScene.id);
  const atFinalScene = activeState.index === SCENE_COUNT - 1;
  const transitionMode = paused
    ? "paused"
    : prefersReducedMotion
      ? "reduced"
      : activeState.input === "keyboard"
        ? "keyboard"
        : "running";

  const paintProgress = useCallback((animate = false) => {
    const line = progressLineRef.current;
    if (!line) return;
    const fraction = Math.min(1, Math.max(0, 1 - (playbackRemaining.current / PRODUCT_STORY_AUTOPLAY_MS)));

    progressAnimation.current?.cancel?.();
    progressAnimation.current = null;
    line.style.transform = `scaleX(${fraction})`;

    if (!animate || typeof line.animate !== "function" || playbackRemaining.current <= 0) return;
    progressAnimation.current = line.animate(
      [
        { transform: `scaleX(${fraction})` },
        { transform: "scaleX(1)" },
      ],
      {
        duration: playbackRemaining.current,
        easing: "linear",
        fill: "forwards",
      },
    );
  }, []);

  const stopPlaybackClock = useCallback((preserveRemaining = true) => {
    if (playbackTimer.current != null) {
      window.clearTimeout(playbackTimer.current);
      playbackTimer.current = null;
    }
    if (preserveRemaining && playbackStartedAt.current != null) {
      const elapsed = Math.max(0, clockNow() - playbackStartedAt.current);
      playbackRemaining.current = Math.max(0, playbackRemaining.current - elapsed);
    }
    playbackStartedAt.current = null;
    paintProgress(false);
  }, [paintProgress]);

  const resetPlaybackClock = useCallback(() => {
    stopPlaybackClock(false);
    playbackRemaining.current = PRODUCT_STORY_AUTOPLAY_MS;
    paintProgress(false);
  }, [paintProgress, stopPlaybackClock]);

  // 2026-08-21: manual navigation WRAPS at the edges (previous from chapter 1 lands on the final
  // chapter and vice versa) so the buttons never dead-end; `travel` is the direction of travel and
  // keeps the slide transition honest across a wrap (index comparison would read a forward wrap
  // as backwards). Autoplay still ENDS at the final chapter — the finite story contract holds.
  const selectScene = useCallback((nextIndex, input = "pointer", resetClock = true, travel) => {
    const wrapped = ((nextIndex % SCENE_COUNT) + SCENE_COUNT) % SCENE_COUNT;
    if (resetClock) resetPlaybackClock();
    if (input !== "autoplay") setFocusPauseLocked(true);
    setFocusIndex(wrapped);
    setActiveState((current) => {
      if (current.index === wrapped) {
        return input === current.input ? current : { ...current, input };
      }
      return {
        index: wrapped,
        direction: travel ?? (input === "autoplay" || wrapped > current.index ? 1 : -1),
        input,
        token: current.token + 1,
      };
    });
  }, [resetPlaybackClock]);

  const warmScene = useCallback((index) => {
    if (typeof Image === "undefined") return;
    const scene = PRODUCT_STORY_SCENES[index];
    if (!scene || scenePreloaders.current.has(scene.image)) return;
    const preloader = new Image();
    preloader.decoding = "async";
    preloader.fetchPriority = "low";
    preloader.onerror = () => scenePreloaders.current.delete(scene.image);
    // PERF-5/VIS-2: mirror the visible <img>'s srcset/sizes BEFORE assigning src so the browser's
    // candidate selection warms the SAME variant it will render. The old `src = master` preloaded
    // the 812–995KB lossless master per scene that the visible img then never displayed.
    // srcset/sizes must be set first — assigning src alone triggers an immediate fetch of the
    // full-resolution file.
    //
    // PERF-9: the visible node is now a <picture> that offers AVIF first, and a bare Image() does
    // NOT do type negotiation — it would warm the WebP rung while the page renders the AVIF one,
    // costing a SECOND download instead of saving one. Parenting the preloader to a detached
    // <picture> carrying the same <source> makes the browser run the full selection algorithm
    // (type + srcset + sizes), so the warmed candidate is exactly the rendered candidate.
    attachAvifSource(preloader, scene.imageAvifSrcSet, PRODUCT_STORY_IMAGE_SIZES);
    preloader.sizes = PRODUCT_STORY_IMAGE_SIZES;
    preloader.srcset = scene.imageSrcSet;
    preloader.src = scene.image;
    scenePreloaders.current.set(scene.image, preloader);
    preloader.decode?.().catch(() => {
      // A decoded preload is an optimisation only; the visible image owns its fallback state.
    });
  }, []);

  const markMediaSettled = useCallback((sceneId) => {
    setSettledSceneIds((current) => {
      if (current.has(sceneId)) return current;
      return new Set([...current, sceneId]);
    });
  }, []);

  const focusTab = (index) => {
    setFocusIndex(index);
    tabRefs.current[index]?.focus();
  };

  const handleTabKeyDown = (event, index) => {
    if (TAB_FOCUS_KEYS.has(event.key)) {
      event.preventDefault();
      if (event.key === "Home") focusTab(0);
      else if (event.key === "End") focusTab(SCENE_COUNT - 1);
      else if (event.key === "ArrowRight") focusTab(Math.min(index + 1, SCENE_COUNT - 1));
      else focusTab(Math.max(index - 1, 0));
      return;
    }
    if (TAB_ACTIVATION_KEYS.has(event.key)) {
      event.preventDefault();
      selectScene(index, "keyboard");
    }
  };

  const selectAdjacent = (offset, event) => {
    selectScene(activeState.index + offset, event?.detail === 0 ? "keyboard" : "pointer", true, offset > 0 ? 1 : -1);
  };

  const motionBlocksAutoPlay = Boolean(prefersReducedMotion || paused);
  const autoPlayRunning = Boolean(
    autoPlayRequested
    && !focusPauseLocked
    && !motionBlocksAutoPlay
    && sectionInView
    && documentVisible
    && !pointerInside
    && activeMediaSettled
    && !atFinalScene
  );
  const autoPlayState = motionBlocksAutoPlay
    ? "motion-paused"
    : atFinalScene
      ? "complete"
      : !autoPlayRequested || focusPauseLocked
      ? "paused"
      : !sectionInView
        ? "out-of-view"
        : !documentVisible
          ? "document-hidden"
          : pointerInside
            ? "interaction-paused"
            : !activeMediaSettled
              ? "media-loading"
              : "playing";

  const rotationAction = motionBlocksAutoPlay
    ? "Motion paused"
    : atFinalScene
      ? "Replay automatic story"
      : autoPlayRequested && !focusPauseLocked
        ? "Pause automatic story"
        : "Start automatic story";

  const toggleAutoPlay = (event) => {
    if (motionBlocksAutoPlay) return;
    const control = event.currentTarget;
    rotationControlPointerDown.current = false;
    if (atFinalScene) {
      resetPlaybackClock();
      setAutoPlayRequested(true);
      setFocusPauseLocked(false);
      selectScene(0, "autoplay", false);
    } else if (autoPlayRequested && !focusPauseLocked) {
      setAutoPlayRequested(false);
    } else {
      setAutoPlayRequested(true);
      setFocusPauseLocked(false);
    }
    if (event.detail > 0) {
      window.requestAnimationFrame(() => control.blur());
    }
  };

  const swipeHandlers = {
    onPointerDown: (event) => {
      if (!event.isPrimary || (event.pointerType === "mouse" && event.button !== 0)) return;
      swipeStart.current = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        time: Date.now(),
      };
    },
    onPointerUp: (event) => {
      const start = swipeStart.current;
      swipeStart.current = null;
      if (!start || start.id !== event.pointerId) return;
      const deltaX = event.clientX - start.x;
      const deltaY = event.clientY - start.y;
      const elapsed = Date.now() - start.time;
      if (elapsed > 800 || Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.25) return;
      selectScene(activeState.index + (deltaX < 0 ? 1 : -1), "pointer", true, deltaX < 0 ? 1 : -1);
    },
    onPointerCancel: () => { swipeStart.current = null; },
  };

  // Keep the ACTIVE tab visible inside the horizontally-scrollable tablist by scrolling the
  // tablist CONTAINER itself — never `scrollIntoView` on the button. Chromium treats a
  // `scrollIntoView` target as the sequential-focus-navigation starting point, so the old
  // mount-time call made the FIRST Tab press on /modules land inside this section's tabpanel,
  // skipping the header and the "Skip to content" link entirely (phase-1 a11y handoff).
  useEffect(() => {
    const button = tabRefs.current[activeState.index];
    const list = button?.closest('[role="tablist"]');
    if (!button || !list || list.scrollWidth <= list.clientWidth) return;
    const listRect = list.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const target = list.scrollLeft + (buttonRect.left - listRect.left) - (list.clientWidth - buttonRect.width) / 2;
    if (typeof list.scrollTo === "function") list.scrollTo({ left: target, behavior: "auto" });
    else list.scrollLeft = target;
  }, [activeState.index]);

  useEffect(() => {
    const stage = stageViewportRef.current;
    if (!stage) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setSectionInView(true);
      setMediaActivated(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      const visible = Boolean(entry?.isIntersecting);
      setSectionInView(visible);
      if (visible) setMediaActivated(true);
    }, { rootMargin: "-15% 0px -15% 0px", threshold: 0 });
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => setDocumentVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  useEffect(() => {
    if (!autoPlayRunning) {
      stopPlaybackClock(true);
      return undefined;
    }

    playbackStartedAt.current = clockNow();
    paintProgress(true);
    playbackTimer.current = window.setTimeout(() => {
      playbackTimer.current = null;
      playbackStartedAt.current = null;
      playbackRemaining.current = 0;
      paintProgress(false);
      if (activeState.index < SCENE_COUNT - 1) {
        selectScene(activeState.index + 1, "autoplay");
      }
    }, playbackRemaining.current);

    return () => stopPlaybackClock(true);
  }, [activeState.index, autoPlayRunning, paintProgress, selectScene, stopPlaybackClock]);

  useEffect(() => {
    if (!activeMediaSettled || !autoPlayRequested || focusPauseLocked || atFinalScene) return undefined;
    const connection = typeof navigator !== "undefined" ? navigator.connection : null;
    if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || "")) return undefined;

    const preload = () => warmScene(activeState.index + 1);
    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(preload, { timeout: 1200 });
      return () => window.cancelIdleCallback?.(idleId);
    }
    const timer = window.setTimeout(preload, 160);
    return () => window.clearTimeout(timer);
  }, [activeMediaSettled, activeState.index, atFinalScene, autoPlayRequested, focusPauseLocked, warmScene]);

  useEffect(() => () => {
    scenePreloaders.current.forEach((preloader) => {
      preloader.onload = null;
      preloader.onerror = null;
    });
    scenePreloaders.current.clear();
    stopPlaybackClock(false);
  }, [stopPlaybackClock]);

  // `/modules#product-story` is a shareable deep link even though this below-fold section is
  // code-split. ModulesHub cannot scroll to a node that has not mounted yet, so the section owns
  // the final hand-off once its DOM is available.
  useEffect(() => {
    if (typeof window === "undefined" || window.location.hash !== `#${sectionId}`) return undefined;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(sectionId)?.scrollIntoView({ block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [sectionId]);

  const focusModuleCatalogue = () => {
    if (typeof window === "undefined") return;
    window.requestAnimationFrame(() => {
      document.getElementById(skipTargetId)?.focus({ preventScroll: true });
    });
  };

  const slideMotion = transitionFor(transitionMode, activeState.direction);
  const previousScene = PRODUCT_STORY_SCENES[(activeState.index - 1 + SCENE_COUNT) % SCENE_COUNT];
  const nextScene = PRODUCT_STORY_SCENES[(activeState.index + 1) % SCENE_COUNT];
  // Announced to assistive tech only — the visible timing chip was removed by product decision
  // 2026-08-21 (the tab progress line already communicates the cadence).
  const playbackStatus = autoPlayState === "playing"
    ? "Playing"
    : autoPlayState === "complete"
      ? "Story complete"
      : autoPlayState === "interaction-paused"
        ? "Paused while you explore"
        : autoPlayState === "media-loading"
          ? "Preparing the next chapter"
          : autoPlayState === "out-of-view" || autoPlayState === "document-hidden"
            ? "Playback waits until this story is visible"
            : autoPlayState === "motion-paused"
              ? "Disabled by your motion preference"
              : "Manual control";

  const handleCarouselFocusCapture = (event) => {
    const isRotationControl = event.target?.dataset?.testid === "product-story-autoplay-toggle";
    if (isRotationControl && rotationControlPointerDown.current) return;
    if (autoPlayRequested) setFocusPauseLocked(true);
  };

  return (
    <section
      ref={sectionRef}
      id={sectionId}
      aria-labelledby={headingId}
      className="relative scroll-mt-24 overflow-clip border-b border-line campus-glass-canvas"
      data-testid="product-story-showcase"
      data-active-scene={activeScene.id}
      data-motion-state={paused ? "paused" : prefersReducedMotion ? "reduced" : "running"}
      data-autoplay-state={autoPlayState}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[32rem] opacity-70"
        style={{ background: "radial-gradient(circle at 72% 12%, rgb(var(--vp-accent) / 0.11), transparent 34%)" }}
        aria-hidden="true"
      />

      <header className="relative mx-auto grid max-w-7xl gap-8 px-5 pb-10 pt-16 sm:px-6 md:pb-12 md:pt-20 lg:px-8 xl:grid-cols-[minmax(0,0.72fr)_minmax(25rem,0.48fr)] xl:items-end xl:pt-24">
        <div>
          <p className="inline-flex min-h-8 items-center gap-2 rounded-full border border-primary/15 bg-primary/[0.06] px-3 text-xs font-semibold text-primary">
            <Layers3 className="h-3.5 w-3.5" aria-hidden="true" />
            <BrandCopy>Vidyapeeth360 product story</BrandCopy>
          </p>
          <h2 id={headingId} className="mt-5 max-w-[17ch] text-balance font-heading text-[clamp(2.35rem,5vw,4.75rem)] font-bold leading-[0.98] tracking-[-0.05em] text-ink">
            One school. Ten connected moments.
          </h2>
        </div>
        <div className="xl:pb-1">
          <p className="max-w-[58ch] text-pretty text-base leading-7 text-ink-2 sm:text-lg sm:leading-8">
            Move through the school day one workflow at a time. Each scene pairs the operational break, the connected response and a clear product view—without hiding approval, provider or rollout boundaries.
          </p>
          <a
            href={`#${skipTargetId}`}
            onClick={focusModuleCatalogue}
            className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-lg px-1 text-sm font-semibold text-accent-active hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring"
          >
            {skipLabel} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </header>

      <div className="relative mx-auto max-w-7xl px-5 pb-20 sm:px-6 md:pb-24 lg:px-8 xl:pb-28">
        <div
          role="group"
          aria-roledescription="carousel"
          aria-labelledby={headingId}
          data-testid="product-story-carousel"
          onMouseEnter={() => setPointerInside(true)}
          onMouseLeave={() => setPointerInside(false)}
          onFocusCapture={handleCarouselFocusCapture}
        >
          <div className="border-y border-line/80 py-3">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 px-1">
              <button
                type="button"
                aria-label={rotationAction}
                aria-disabled={motionBlocksAutoPlay}
                onPointerDown={() => { rotationControlPointerDown.current = true; }}
                onPointerCancel={() => { rotationControlPointerDown.current = false; }}
                onClick={toggleAutoPlay}
                data-testid="product-story-autoplay-toggle"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-line bg-surface px-4 text-[13px] font-semibold text-ink-2 transition-[transform,opacity] duration-150 ease-premium active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas aria-disabled:cursor-not-allowed aria-disabled:opacity-60"
              >
                {atFinalScene || !autoPlayRequested || focusPauseLocked || motionBlocksAutoPlay ? (
                  <Play className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Pause className="h-4 w-4" aria-hidden="true" />
                )}
                <span>{rotationAction}</span>
              </button>
              <p className="sr-only" aria-live="polite" data-testid="product-story-playback-status">
                {playbackStatus}
              </p>
            </div>
            <div
              id={tabsId}
              role="tablist"
              aria-label="Product story chapters"
              aria-orientation="horizontal"
              className="product-story-tabs flex snap-x snap-mandatory gap-1 overflow-x-auto overscroll-x-contain pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
            {PRODUCT_STORY_SCENES.map((scene, index) => {
              const selected = activeState.index === index;
              return (
                <button
                  key={scene.id}
                  ref={(node) => { tabRefs.current[index] = node; }}
                  id={`product-story-tab-${scene.id}`}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls={`product-story-panel-${scene.id}`}
                  tabIndex={focusIndex === index ? 0 : -1}
                  onFocus={() => {
                    setFocusIndex(index);
                    warmScene(index);
                  }}
                  onPointerEnter={() => warmScene(index)}
                  onKeyDown={(event) => handleTabKeyDown(event, index)}
                  onClick={(event) => selectScene(index, event.detail === 0 ? "keyboard" : "pointer")}
                  className={cn(
                    "group relative min-h-11 shrink-0 snap-center rounded-full px-3.5 text-left text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas sm:px-4",
                    selected ? "bg-ink text-canvas" : "text-ink-2 hover:bg-surface hover:text-ink",
                  )}
                  data-testid={`product-story-tab-${scene.id}`}
                >
                  <span className={cn("mr-2 tabular-nums", selected ? "text-canvas/65" : "text-ink-subtle")}>
                    {storyNumber(index)}
                  </span>
                  {scene.eyebrow}
                  {selected && (
                    <span
                      className="pointer-events-none absolute inset-x-3 bottom-1 h-0.5 overflow-hidden rounded-full bg-canvas/25"
                      aria-hidden="true"
                    >
                      <span
                        ref={progressLineRef}
                        className="block h-full origin-left bg-canvas will-change-transform"
                        style={{ transform: "scaleX(0)" }}
                        data-testid="product-story-progress"
                        data-progress-state={autoPlayState}
                      />
                    </span>
                  )}
                </button>
              );
            })}
            </div>
          </div>

        <div
          ref={stageViewportRef}
          className="product-story-panels pt-10 md:pt-12"
          aria-live={autoPlayRunning ? "off" : "polite"}
          aria-atomic="false"
        >
          {PRODUCT_STORY_SCENES.map((scene, index) => {
            const active = index === activeState.index;
            return (
              <div
                key={scene.id}
                id={`product-story-panel-${scene.id}`}
                role="tabpanel"
                aria-labelledby={`product-story-tab-${scene.id}`}
                tabIndex={active ? 0 : -1}
                hidden={!active}
                data-scene-index={index}
                data-scene-id={scene.id}
                data-active={active ? "true" : "false"}
              >
                <m.div
                  key={active ? `${scene.id}-${activeState.token}` : scene.id}
                  {...(active ? slideMotion : { initial: false })}
                  className="product-story-active-stage grid min-w-0 gap-10 xl:grid-cols-[minmax(21rem,0.37fr)_minmax(0,0.63fr)] xl:items-center xl:gap-10"
                  data-testid={active ? "product-story-active-stage" : undefined}
                  data-active-scene={scene.id}
                  data-transition-mode={active ? transitionMode : undefined}
                >
                  <SceneCopy scene={scene} index={index} onBookDemo={onBookDemo} catalogueTargetId={catalogueTargetId} />
                  <SceneMediaStage
                    scene={scene}
                    index={index}
                    mode={active ? transitionMode : "paused"}
                    direction={activeState.direction}
                    mediaEnabled={active && mediaActivated}
                    onMediaSettled={markMediaSettled}
                    swipeHandlers={active ? swipeHandlers : undefined}
                  />
                </m.div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 flex flex-col gap-5 border-t border-line pt-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-subtle">Current chapter</p>
            <p className="mt-1 truncate text-sm font-semibold text-ink">
              <span className="mr-2 tabular-nums" style={{ color: storyAccent(activeScene) }}>
                {storyNumber(activeState.index)} / {SCENE_COUNT}
              </span>
              {activeScene.eyebrow}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="min-h-12 min-w-12 rounded-full px-4 transition-[transform,opacity] duration-150 ease-premium"
              onClick={(event) => selectAdjacent(-1, event)}
              aria-label={`Previous: ${previousScene.eyebrow}`}
              data-testid="product-story-previous"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Previous chapter</span>
            </Button>
            <Button
              type="button"
              variant="primary"
              size="lg"
              className="min-h-12 min-w-12 rounded-full px-4 transition-[transform,opacity] duration-150 ease-premium"
              onClick={(event) => selectAdjacent(1, event)}
              aria-label={`Next: ${nextScene.eyebrow}`}
              data-testid="product-story-next"
            >
              <span className="hidden sm:inline">Next chapter</span>
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
        </div>
      </div>
    </section>
  );
}
